import { BasePlugin, UppyFile, getFinishedUpload } from '@buffer-mono/uploader'
import type {
  UploaderUppy,
  PluginOpts,
  BaseUppyFile,
  BufferUpload,
  UploaderBody,
  UploaderMeta,
  Upload,
} from '@buffer-mono/uploader'

import { toBufferUpload } from './toBufferUpload'

export interface TusUploadsUppyPluginOptions extends PluginOpts {
  onComplete: (files: BufferUpload) => void
  onError: (file: Upload, error: Error) => void
}

// The upload service writes the tracking record and probes the media itself, so
// a finished upload needs no follow-up call — everything the composer reads
// arrives in the finish response.
export default class TusUploadsUppyPlugin extends BasePlugin<
  TusUploadsUppyPluginOptions,
  UploaderMeta,
  UploaderBody
> {
  private onComplete: ((file: BufferUpload) => void) | undefined
  private onError: ((file: Upload, error: Error) => void) | undefined

  constructor(uppy: UploaderUppy, opts?: TusUploadsUppyPluginOptions) {
    super(uppy, opts)

    this.id = 'TusUploadsUppyPlugin'
    this.type = 'modifier'
    this.onComplete = opts?.onComplete
    this.onError = opts?.onError

    this.handleUploadSuccess = this.handleUploadSuccess.bind(this)
  }

  private async handleUploadSuccess(
    file: BaseUppyFile | undefined,
  ): Promise<void> {
    // Uppy can fire upload-success without a file in transient states
    // (autoProceed cleanup, file removed mid-flight, plugin teardown).
    if (!file) return

    // A file's last chunk often lands before abort() takes effect on cancel, so
    // upload-success still fires for a file the user has already cancelled. Bail
    // before triggering the downstream draft-image insertion.
    const refreshedFile = this.uppy.getFile(file.id)
    if (!refreshedFile || !UppyFile.isUploadedUppyFile(refreshedFile)) return

    const upload = UppyFile.toUpload(refreshedFile)

    try {
      const finished = getFinishedUpload(refreshedFile)

      if (!finished) {
        throw new Error('The upload service reported no finished upload.')
      }

      // No thumbnail arrives out of band on this transport: the service
      // reports the one it rendered with the finished upload.
      this.onComplete?.(toBufferUpload(upload, finished))
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      err.message = `Upload failed for ${file.name}. ${err.message}`
      this.onError?.(upload, err)
    }
  }

  install(): void {
    this.uppy.on('upload-success', this.handleUploadSuccess)
  }

  uninstall(): void {
    this.uppy.off('upload-success', this.handleUploadSuccess)
  }
}
